import {
  Component,
  EventEmitter,
  Input,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  Discipline,
  DisciplineSubscale,
  DisciplineSubscaleNew, DisciplineSubscaleType
} from '../../models/models.lib';
import {
  DialogCloseResult,
  DialogService,
  DialogsModule,
  WindowModule,
  WindowRef,
} from '@progress/kendo-angular-dialog';
import { Observable, Subscription } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { MeService } from '../../auth/me.service';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ToastrService } from 'ngx-toastr';
import { faEdit, faPlusCircle, faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { AppEnities, CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { DisciplineSubscaleService } from '../discipline-subscale.service';
import { setTitle } from '../../common/utils.lib';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { DisciplineSubscaleAddComponent } from '../discipline-subscale-add.component/discipline-subscale-add.component';

const { DISCIPLINE } = AppEnities;

@Component({
  selector: 'casi-discipline-subscale-list-win',
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './discipline-subscale-list-win.html',
  styleUrl: './discipline-subscale-list-win.scss'
})
export class DisciplineSubscaleListWin implements OnInit, OnDestroy {

  private _discipline: Discipline | undefined;

  // @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set discipline(val: Discipline) {
    this._discipline = val;
  }
  get discipline(): Discipline | undefined {
    return this._discipline;
  };

  @Output() onCancel = new EventEmitter<void>();

  private _windowRef: WindowRef | undefined;

  public view: Observable<DisciplineSubscale[]> = new Observable<DisciplineSubscale[]>();
  public rawData: DisciplineSubscale[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: DisciplineSubscale[] = filterBy(this.rawData, this.filter);

  public currentItem: DisciplineSubscaleType = DisciplineSubscaleNew.create(this.discipline);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public editWinOpen: Boolean = false;
  public formMode: FormModes = FormModes.NOTSET;

  private _subs: Subscription[] = [];

  public canUpdate: boolean = false;

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: DisciplineSubscaleService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    { field: "sort_order", dir: "asc" },
  ];

  //#region Ng Event handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');

    const { can_update } = this.me.allICanDo(DISCIPLINE);
    this.canUpdate = can_update;

    if (this.discipline)
      this.dataService.query(this.discipline.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid functions

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region Public functions

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onCancel.emit();
  }

  public setActive(dataItem: any): void {
    dataItem.active = !dataItem.active;
    this.logger.debug('setActive:dataItem: ', dataItem);
    this.currentItem = DisciplineSubscale.create(dataItem);
    this.saveActive(this.currentItem);
  }

  public onAddClick(): void {
    this.logger.debug("onAddClick...");
    this.currentItem = DisciplineSubscaleNew.create(this.discipline);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  public onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = DisciplineSubscale.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  public onUpClick(dataItem: any): void {
    this.logger.debug("onUpClick: ", dataItem);
    this.dataService.moveUp(dataItem.id)
      .then(resp => {
        this.logger.debug('saveDiscipline:update:resp: ', resp);
        this.notify("Discipline Subscale Updated");
        if (this._discipline)
          this.dataService.query(this._discipline.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Discipline Subscale", true);
      });
  }

  public onDownClick(dataItem: any): void {
    this.logger.debug("onUpClick: ", dataItem);
    this.dataService.moveDown(dataItem.id)
      .then(resp => {
        this.logger.debug('saveDiscipline:update:resp: ', resp);
        this.notify("Discipline Subscale Updated");
        if (this._discipline)
          this.dataService.query(this._discipline.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Discipline Subscale", true);
      });
  }

  //#endregion

  //#region Private functions

  private openAddEditDialog(mode: FormModes, dataItem: DisciplineSubscaleType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: DisciplineSubscaleAddComponent,
      minWidth: 320,
      width: 450,
    });

    const contentRef = dialogRef.content.instance as DisciplineSubscaleAddComponent;
    contentRef.title = setTitle(mode, 'DISCIPLINE SUBSCALE');
    contentRef.disciplineSubscale = dataItem;
    contentRef.mode = mode;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.disciplineSubscaleForm.value);
        this.handleClose(contentRef.disciplineSubscaleForm.value, mode);
      }
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    let dup: boolean = false;
    if (data) {
      let inUse = this.rawData.find(r => r.subscale_description === data.subscale_description);
      this.logger.debug('handleClose...inUse', inUse);
      if (inUse) {
        if (mode === FormModes.NEW) {
          dup = true;
        } else {
          dup = inUse.id !== data.id;
        }
      }
      if (dup) {
        this.logger.warn(`handleClose: discipline: ${data.subscale_description} already in use `);
        this.openErrorDailog("Duplicate Discipline Subscale Description", "That Discipline Subscale already exists!", "Close and Retry")
      } else
        this.save(data, mode);
    }
  }

  private save(disciplineSubscale: any, mode: FormModes): void {
    this.logger.debug('saveDiscipline...', disciplineSubscale, mode);
    if (mode === FormModes.EDIT) {
      this.dataService.update(disciplineSubscale.id, disciplineSubscale)
        .then(resp => {
          this.logger.debug('saveDiscipline:update:resp: ', resp);
          this.notify("Discipline Subscale Updated");
          this.closeWindowRef();
          if (this._discipline)
            this.dataService.query(this._discipline.id);
        })
        .catch(err => {
          this.logger.error('saveDiscipline:update:err: ', err);
          this.notify("Error Updating Discipline Subscale", true);
        });
    } else {
      this.dataService.add(disciplineSubscale)
        .then(resp => {
          this.logger.debug('saveDiscipline:add:resp: ', resp);
          this.notify("Discipline Subscale Added");
          this.closeWindowRef();
          if (this._discipline)
            this.dataService.query(this._discipline.id);
        })
        .catch(err => {
          this.logger.error('saveDiscipline:add:err: ', err);
          this.notify("Error Creating Discipline Subscale", true);
        });
    }
  }

  private saveActive(disciplineSubscale: any): void {
    this.logger.debug('saveDiscipline...', disciplineSubscale);
    this.dataService.update(disciplineSubscale.id, disciplineSubscale)
      .then(resp => {
        this.logger.debug('saveDiscipline:update:resp: ', resp);
        this.notify(disciplineSubscale.active ? "Discipline Subscale Activated" : "Discipline Subscale Deactivated");
        this.closeWindowRef();
        if (this._discipline)
          this.dataService.query(this._discipline.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Updating Discipline Subscale", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion

}
